import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { PitchDeckStatic } from '../../components/pitch-deck-static/pitch-deck-static';
import { ApplyForBeta } from '../../components/apply-for-beta/apply-for-beta';

/** MP4 (H.264) — browsers often refuse autoplay of QuickTime .mov containers. */
const PITCH_VIDEO_SRC = '/assets/hebern-pitch.mp4';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, PitchDeckStatic, ApplyForBeta],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pitchVideoHost =
    viewChild<ElementRef<HTMLElement>>('pitchVideoHost');
  private readonly pitchVideoEl =
    viewChild<ElementRef<HTMLVideoElement>>('pitchVideo');

  /** Set only when the section is near the viewport so the file is not requested on initial load. */
  readonly pitchVideoSrc = signal<string | null>(null);

  playPitchVideo(event?: Event): void {
    const video =
      (event?.target as HTMLVideoElement | undefined) ??
      this.pitchVideoEl()?.nativeElement;
    if (!video) {
      return;
    }

    // Angular's `muted` attribute is unreliable for media elements — set the DOM property.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const attempt = () => {
      void video.play().catch(() => {
        // Autoplay may still be blocked; controls remain available.
      });
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attempt();
    } else {
      video.addEventListener('canplay', attempt, { once: true });
    }
  }

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const host = this.pitchVideoHost()?.nativeElement;
      if (!host) {
        return;
      }

      const armSrc = () => {
        this.pitchVideoSrc.set(PITCH_VIDEO_SRC);
        // Wait for @if to create the <video>, then force muted play.
        setTimeout(() => this.playPitchVideo(), 0);
      };

      if (!('IntersectionObserver' in window)) {
        armSrc();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            return;
          }
          armSrc();
          observer.disconnect();
        },
        { root: null, rootMargin: '200px 0px', threshold: 0.01 }
      );

      observer.observe(host);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }
}
